import { useRef, useState } from 'react';

const isImage = (f: File): boolean =>
  f.type.startsWith('image/') && !f.type.endsWith('webp');

interface ToWebPOptions {
  quality?: number;
}

async function toWebP(file: File, options?: ToWebPOptions): Promise<File> {
  const url = URL.createObjectURL(file);
  const img: HTMLImageElement = await new Promise((resolve, reject) => {
    const image = new window.Image();
    image.onload = () => resolve(image);
    image.onerror = (err) => reject(err);
    image.src = url;
  });

  const canvas: HTMLCanvasElement = document.createElement('canvas');
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    URL.revokeObjectURL(url);
    throw new Error('Não foi possível obter o contexto do canvas');
  }
  ctx.drawImage(img, 0, 0);

  const quality = (options?.quality ?? 80) / 100;
  const dataUrl = canvas.toDataURL('image/webp', quality);

  const res = await fetch(dataUrl);
  const blob = await res.blob();
  URL.revokeObjectURL(url);

  return new File([blob], file.name.replace(/\.[^.]+$/, '.webp'), {
    type: 'image/webp',
  });
}

// Função para detectar magic number (simplificada para imagens e PDF, expanda conforme necessário)
async function detectMimeType(file: File): Promise<string | null> {
  const signatures: { [key: string]: Uint8Array } = {
    'image/png': new Uint8Array([0x89, 0x50, 0x4E, 0x47]),
    'image/jpeg': new Uint8Array([0xFF, 0xD8, 0xFF]),
    'application/pdf': new Uint8Array([0x25, 0x50, 0x44, 0x46]),
    // Adicione mais conforme necessário
  };
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const arr = new Uint8Array(reader.result as ArrayBuffer);
      for (const [mime, sig] of Object.entries(signatures)) {
        if (arr.slice(0, sig.length).every((b, i) => b === sig[i])) {
          return resolve(mime);
        }
      }
      resolve(null);
    };
    reader.readAsArrayBuffer(file.slice(0, 8));
  });
}

// Lista de extensões perigosas
const dangerousExts = ['.exe', '.bat', '.cmd', '.js', '.vbs', '.scr', '.pif', '.com', '.cpl', '.msc', '.jar', '.msi', '.wsf', '.ps1'];

interface UseUploadHandlerProps {
  accept?: string[];
  maxFiles?: number;
  onFilesSelected: (files: File[], error?: Error | string | null) => void; // <-- Corrigido aqui
  autoUpload?: boolean;
  onUpload?: (file: File, onProgress: (percent: number) => void) => Promise<unknown>;
  convertToWebP?: boolean;
  onProcessFile?: (file: File) => Promise<File>;
  customValidation?: (files: File[]) => string | undefined;
  setUploadProgress?: (progress: number[] | ((prev: number[]) => number[])) => void;
  setShowSuccess?: (show: boolean) => void;
  setCustomError?: (err: string | null) => void;
  maxFilesPerDirectory?: number; // NOVO
}

export function useUploadHandler({
  accept,
  maxFiles = 1,
  onFilesSelected,
  autoUpload = false,
  onUpload,
  convertToWebP = true,
  onProcessFile,
  customValidation,
  setUploadProgress,
  setShowSuccess,
  setCustomError,
  maxFilesPerDirectory,
}: UseUploadHandlerProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const acceptStr = accept?.join(',') ?? undefined;

  // Função para agrupar arquivos por pasta
  function groupByDirectory(files: File[]): Record<string, File[]> {
    const map: Record<string, File[]> = {};
    files.forEach((file: File & { webkitRelativePath?: string }) => {
      const dir = file.webkitRelativePath
        ? file.webkitRelativePath.split('/').slice(0, -1).join('/')
        : '';
      if (!map[dir]) map[dir] = [];
      map[dir].push(file);
    });
    return map;
  }

  // Validação robusta
  const validate = async (files: FileList | File[]): Promise<{ valid: File[], errors: string[] }> => {
    let f: File[] = Array.from(files);
    const errors: string[] = [];

    // Extensão perigosa
    for (const file of f) {
      const ext = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
      if (dangerousExts.includes(ext)) {
        errors.push(`Arquivo "${file.name}" possui extensão perigosa (${ext}).`);
      }
    }

    // Detecta MIME real (apenas para imagens/pdf, expanda conforme necessário)
    for (const file of f) {
      const realMime = await detectMimeType(file);
      if (realMime && file.type && realMime !== file.type) {
        errors.push(`Arquivo "${file.name}" possui tipo real diferente do informado (${file.type} ≠ ${realMime}).`);
      }
    }

    // Limite por diretório
    if (maxFilesPerDirectory) {
      const grouped = groupByDirectory(f);
      for (const [dir, filesArr] of Object.entries(grouped)) {
        if (filesArr.length > maxFilesPerDirectory) {
          errors.push(
            `A pasta "${dir || 'raiz'}" excede o limite de ${maxFilesPerDirectory} arquivos (${filesArr.length}).`
          );
        }
      }
    }

    // Filtros de accept
    if (accept?.length) {
      f = f.filter((file: File) =>
        accept.some((a: string) =>
          a.startsWith('.') ? file.name.endsWith(a) : file.type === a,
        ),
      );
      if (f.length === 0 && Array.from(files).length > 0) {
        errors.push(`Tipo de arquivo inválido. Aceitos: ${accept.join(', ')}`);
      }
    }

    // Limite total
    if (f.length > maxFiles) {
      errors.push(`Você pode enviar no máximo ${maxFiles} arquivo(s).`);
    }

    return { valid: f, errors };
  };

  const validateFiles = (files: File[]) => {
    if (typeof customValidation === "function") {
      const err = customValidation(files);
      setCustomError?.(err || null);
      return !err;
    }
    setCustomError?.(null);
    return true;
  };

  const handleFiles = async (inputFiles: FileList | File[]): Promise<void> => {
    setLoading(true);
    setError(null);

    const { valid: validFiles, errors } = await validate(inputFiles);

    if (errors.length > 0) {
      setError(errors.join('\n'));
      if (inputRef.current) (inputRef.current as HTMLInputElement).value = '';
      onFilesSelected([], errors.join('\n'));
      setLoading(false);
      return;
    }

    if (validFiles.length === 0 && Array.from(inputFiles).length > 0) {
      setLoading(false);
      if (inputRef.current) (inputRef.current as HTMLInputElement).value = '';
      // Propague o erro detalhado:
      onFilesSelected([], error || "Tipo de arquivo inválido.");
      return;
    }

    const processedPromises: Promise<File>[] = validFiles.map(async (f: File) => {
      if (isImage(f)) {
        try {
          return await toWebP(f);
        } catch (conversionError) {
          setError((prevError) => {
            const newError = `Falha ao converter ${f.name}. O original será usado.`;
            return prevError ? `${prevError}\n${newError}` : newError;
          });
          return f;
        }
      }
      return f;
    });

    try {
      const processed: File[] = await Promise.all(processedPromises);
      onFilesSelected(processed.filter((f) => f !== null), null);
    } catch (e) {
      setError("Ocorreu um erro ao processar os arquivos.");
      onFilesSelected([], e instanceof Error ? e : String(e));
    }

    if (inputRef.current) {
      (inputRef.current as HTMLInputElement).value = '';
    }
    setLoading(false);
  };

  // Lida com seleção de arquivos
  const handleFilesSelected = async (files: File[] | FileList) => {
    let filesArr = Array.from(files);

    // Processamento customizado ou conversão para WebP
    if (onProcessFile) {
      filesArr = await Promise.all(filesArr.map(onProcessFile));
    } else if (convertToWebP) {
      filesArr = await Promise.all(
        filesArr.map(async (file) => {
          if (isImage(file)) {
            try {
              return await toWebP(file);
            } catch (e) {
              setError((prev) => (prev ? prev + '\n' : '') + `Falha ao converter ${file.name}. O original será usado.`);
              return file;
            }
          }
          return file;
        })
      );
    }

    // Validação customizada
    if (!validateFiles(filesArr)) {
      setShowSuccess?.(false);
      return;
    }

    onFilesSelected(filesArr);

    // Auto-upload se habilitado
    if (autoUpload && onUpload && setUploadProgress) {
      setUploadProgress(Array(filesArr.length).fill(0));
      await Promise.all(
        filesArr.map((file: File, idx: number) =>
          onUpload(file, (percent: number) => {
            setUploadProgress((prev: number[]) => {
              const next = [...prev];
              next[idx] = percent;
              return next;
            });
          })
        )
      );
      setShowSuccess?.(true);
      setTimeout(() => setShowSuccess?.(false), 2000);
    }
  };

  return {
    inputRef,
    dragActive,
    setDragActive,
    error,
    loading,
    acceptStr,
    handleFiles,
    handleFilesSelected,
  };
}